import React, { useState, useEffect } from 'react';
import { Phone, Menu, X, ArrowRight, Zap } from 'lucide-react';

interface NavbarProps {
  currentPage?: 'landing' | 'calculator';
  onNavigatePage?: (page: 'landing' | 'calculator') => void;
}

export const Navbar: React.FC<NavbarProps> = ({ currentPage = 'landing', onNavigatePage }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    setMobileMenuOpen(false);
    if (currentPage !== 'landing' && onNavigatePage) {
      onNavigatePage('landing');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
      return;
    }

    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleGoCalculator = () => {
    setMobileMenuOpen(false);
    if (onNavigatePage) {
      onNavigatePage('calculator');
    }
  };

  const handleGoHome = (e: React.MouseEvent) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    if (onNavigatePage) {
      onNavigatePage('landing');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || currentPage === 'calculator'
          ? 'bg-white/95 backdrop-blur-md border-b border-slate-200/80 shadow-md py-3.5'
          : 'bg-transparent border-b border-white/10 py-4.5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          
          {/* Brand Logo (Left) */}
          <a href="#" onClick={handleGoHome} className="flex items-center gap-2.5 group">
            <div className="h-10 w-10 sm:h-11 sm:w-11 rounded-xl bg-white p-1 shadow-sm border border-slate-100/50 flex items-center justify-center overflow-hidden group-hover:scale-105 transition-transform shrink-0">
              <img src="/plus_sd_logo.png" alt="플러스에스디" className="w-full h-full object-contain" />
            </div>
            <div className="flex flex-col">
              <span className={`text-base sm:text-lg font-black tracking-tight flex items-center gap-1 transition-colors leading-tight ${
                scrolled || currentPage === 'calculator' ? 'text-slate-900' : 'text-white'
              }`}>
                플러스에스디
              </span>
              <span className={`text-[9px] font-extrabold tracking-widest uppercase transition-colors leading-none mt-0.5 ${
                scrolled || currentPage === 'calculator' ? 'text-slate-500' : 'text-slate-300'
              }`}>
                SOLAR & ROOF SOLUTION
              </span>
            </div>
          </a>

          {/* Desktop Center Navigation Links */}
          <div className={`hidden md:flex items-center gap-7 text-xs sm:text-sm font-extrabold transition-colors ${
            scrolled || currentPage === 'calculator' ? 'text-slate-700' : 'text-white/90'
          }`}>
            <button
              onClick={() => scrollToSection('growth-section')}
              className="hover:text-sky-400 transition-colors cursor-pointer tracking-tight"
            >
              로또지붕 리포트
            </button>
            <button
              onClick={() => scrollToSection('pain-points')}
              className="hover:text-sky-400 transition-colors cursor-pointer tracking-tight"
            >
              ESG 내일의 변화
            </button>
            <button
              onClick={() => scrollToSection('benefits')}
              className="hover:text-sky-400 transition-colors cursor-pointer tracking-tight"
            >
              수리비 지원 혜택
            </button>
            <button
              onClick={() => scrollToSection('eligibility')}
              className="hover:text-sky-400 transition-colors cursor-pointer tracking-tight"
            >
              설치 조건 & 절차
            </button>
            
            {/* Direct Page Switch to Calculator */}
            <button
              onClick={handleGoCalculator}
              className="flex items-center gap-1.5 text-sky-500 hover:text-sky-600 transition-colors cursor-pointer font-black"
            >
              <Zap className="w-3.5 h-3.5 fill-sky-500 text-sky-500" />
              <span>수익 계산기</span>
            </button>
          </div>

          {/* Contact & CTA (Right) */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="tel:01027671449"
              className={`flex items-center gap-2 text-xs font-black px-3.5 py-2 rounded-xl border transition-all shadow-sm ${
                scrolled || currentPage === 'calculator'
                  ? 'text-sky-600 bg-sky-50 border-sky-200 hover:bg-sky-100'
                  : 'text-white bg-white/10 border-white/20 hover:bg-white/20 backdrop-blur-sm'
              }`}
            >
              <Phone className={`w-3.5 h-3.5 ${scrolled || currentPage === 'calculator' ? 'text-sky-600' : 'text-sky-400'}`} />
              <span>010-2767-1449</span>
            </a>
            <button
              onClick={handleGoCalculator}
              className="px-4.5 py-2 rounded-xl bg-sky-500 hover:bg-sky-600 text-white font-black text-xs shadow-lg shadow-sky-500/25 active:scale-95 transition-all cursor-pointer flex items-center gap-1.5"
            >
              <span>실시간 계산기</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Mobile Menu Toggle Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden p-2 rounded-xl transition-colors ${
              scrolled || currentPage === 'calculator' ? 'text-slate-700 hover:bg-slate-100' : 'text-white hover:bg-white/10'
            }`}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>

        </div>
      </div>

      {/* Mobile Drawer Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-b border-slate-200 px-4 pt-4 pb-6 space-y-3 shadow-xl animate-expand-down">
          <button
            onClick={() => scrollToSection('growth-section')}
            className="block w-full text-left py-2.5 text-sm font-bold text-slate-800 hover:text-sky-600"
          >
            로또지붕 리포트
          </button>
          <button
            onClick={() => scrollToSection('pain-points')}
            className="block w-full text-left py-2.5 text-sm font-bold text-slate-800 hover:text-sky-600"
          >
            3대 핵심 고민
          </button>
          <button
            onClick={() => scrollToSection('benefits')}
            className="block w-full text-left py-2.5 text-sm font-bold text-slate-800 hover:text-sky-600"
          >
            수리비 지원 혜택
          </button>
          <button
            onClick={() => scrollToSection('eligibility')}
            className="block w-full text-left py-2.5 text-sm font-bold text-slate-800 hover:text-sky-600"
          >
            설치 조건 & 절차
          </button>
          <button
            onClick={handleGoCalculator}
            className="w-full text-left py-2.5 text-sm font-black text-sky-600 flex items-center justify-between"
          >
            <span>⚡ 수익 계산기</span>
          </button>
          
          <div className="pt-2 flex flex-col gap-2">
            <a
              href="tel:01027671449"
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-sky-50 border border-sky-200 text-sky-600 font-extrabold text-xs"
            >
              <Phone className="w-4 h-4 text-sky-600" />
              <span>010-2767-1449 전화상담</span>
            </a>
            <button
              onClick={handleGoCalculator}
              className="w-full py-3 rounded-xl bg-sky-500 text-white font-extrabold text-xs shadow-md shadow-sky-500/25"
            >
              실시간 수익 계산기 시작하기
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

